import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';

const API = process.env.REACT_APP_API_URL || '/api';

export default function CampaignsTab({ settings, t }) {
  // ALL state owned locally
  const [campaigns, setCampaigns] = useState([]);
  const [campaignView, setCampaignView] = useState('list'); // list, editor, detail
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState({ subject_en: '', subject_fr: '', body_en: '', body_fr: '' });
  const [campaignSaving, setCampaignSaving] = useState(false);
  const [campaignLangTab, setCampaignLangTab] = useState('fr');
  const [campaignTranslating, setCampaignTranslating] = useState(false);
  const [campaignMsg, setCampaignMsg] = useState({ type: '', text: '' });
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [recipientCount, setRecipientCount] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [quotaInfo, setQuotaInfo] = useState(null);

  // Helpers
  const formatDate = (date) => new Date(date).toLocaleDateString();
  const formatDateTime = (date) => new Date(date).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  // ========== CAMPAIGNS ==========
  const loadCampaigns = async () => {
    try {
      const [res, countRes, quotaRes] = await Promise.allSettled([
        axios.get(`${API}/admin/campaigns`),
        axios.get(`${API}/admin/campaigns/recipient-count`),
        axios.get(`${API}/admin/campaigns/quota`)
      ]);
      if (res.status === 'fulfilled') setCampaigns(res.value.data);
      if (countRes.status === 'fulfilled') setRecipientCount(countRes.value.data.count);
      if (quotaRes.status === 'fulfilled') setQuotaInfo(quotaRes.value.data);
    } catch (err) { /* ignore */ }
  };

  const resumeCampaign = async (id) => {
    try {
      await axios.post(`${API}/admin/campaigns/${id}/resume`);
      setCampaignMsg({ type: 'success', text: 'Campaign resume started' });
      loadCampaigns();
    } catch (err) {
      setCampaignMsg({ type: 'danger', text: err.response?.data?.error || 'Failed to resume campaign' });
    }
  };

  const openCampaignEditor = (campaign = null) => {
    setCampaignLangTab('fr');
    setCampaignMsg({ type: '', text: '' });
    if (campaign) {
      setEditingCampaign(campaign);
      setCampaignForm({ subject_en: campaign.subject_en || '', subject_fr: campaign.subject_fr || '', body_en: campaign.body_en || '', body_fr: campaign.body_fr || '' });
    } else {
      setEditingCampaign(null);
      setCampaignForm({ subject_en: '', subject_fr: '', body_en: '', body_fr: '' });
    }
    setCampaignView('editor');
  };

  const saveCampaignDraft = async () => {
    setCampaignSaving(true);
    setCampaignMsg({ type: '', text: '' });
    try {
      if (editingCampaign) {
        await axios.put(`${API}/admin/campaigns/${editingCampaign.id}`, campaignForm);
      } else {
        const res = await axios.post(`${API}/admin/campaigns`, campaignForm);
        setEditingCampaign(res.data);
      }
      setCampaignMsg({ type: 'success', text: t.admin.campaigns?.saveDraft || 'Draft saved' });
      loadCampaigns();
    } catch (err) {
      setCampaignMsg({ type: 'danger', text: err.response?.data?.error || 'Error saving' });
    } finally {
      setCampaignSaving(false);
    }
  };

  const sendCampaignTest = async (id) => {
    try {
      await axios.post(`${API}/admin/campaigns/${id}/test`);
      setCampaignMsg({ type: 'success', text: t.admin.campaigns?.testSent || 'Test email sent!' });
    } catch (err) {
      setCampaignMsg({ type: 'danger', text: err.response?.data?.message || 'Test failed' });
    }
  };

  const sendCampaignNow = async (id) => {
    if (!window.confirm(t.admin.campaigns?.confirmSend || 'Send this campaign to all subscribed customers?')) return;
    try {
      await axios.post(`${API}/admin/campaigns/${id}/send`);
      setCampaignMsg({ type: 'success', text: 'Campaign sending started' });
      setCampaignView('list');
      loadCampaigns();
    } catch (err) {
      setCampaignMsg({ type: 'danger', text: err.response?.data?.error || 'Send failed' });
    }
  };

  const scheduleCampaign = async (id) => {
    if (!scheduleDate) return;
    try {
      await axios.post(`${API}/admin/campaigns/${id}/schedule`, { scheduled_at: scheduleDate });
      setCampaignMsg({ type: 'success', text: (t.admin.campaigns?.scheduledFor || 'Scheduled for') + ' ' + new Date(scheduleDate).toLocaleString() });
      setCampaignView('list');
      loadCampaigns();
    } catch (err) {
      setCampaignMsg({ type: 'danger', text: err.response?.data?.error || 'Schedule failed' });
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm(t.admin.campaigns?.confirmDelete || 'Delete this draft campaign?')) return;
    try {
      await axios.delete(`${API}/admin/campaigns/${id}`);
      setCampaignView('list');
      loadCampaigns();
    } catch (err) { /* ignore */ }
  };

  const viewCampaignDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/campaigns/${id}`);
      setCampaignDetail(res.data);
      setCampaignView('detail');
    } catch (err) { /* ignore */ }
  };

  const campaignAutoTranslate = async (sourceLang) => {
    setCampaignTranslating(true);
    try {
      const targetLang = sourceLang === 'fr' ? 'en' : 'fr';
      const texts = sourceLang === 'fr'
        ? [campaignForm.subject_fr, campaignForm.body_fr]
        : [campaignForm.subject_en, campaignForm.body_en];
      const res = await axios.post(`${API}/admin/translate`, { texts, source_lang: sourceLang, target_lang: targetLang });
      const translated = res.data.translated;
      if (targetLang === 'en') {
        setCampaignForm(prev => ({ ...prev, subject_en: translated[0] || prev.subject_en, body_en: translated[1] || prev.body_en }));
      } else {
        setCampaignForm(prev => ({ ...prev, subject_fr: translated[0] || prev.subject_fr, body_fr: translated[1] || prev.body_fr }));
      }
    } catch (err) { /* ignore */ }
    setCampaignTranslating(false);
  };

  const campaignStatusBadge = (status) => {
    const colors = { draft: 'secondary', scheduled: 'info', sending: 'warning', sent: 'success', failed: 'danger', paused: 'warning' };
    const labels = { draft: t.admin.campaigns?.draft || 'Draft', scheduled: t.admin.campaigns?.scheduled || 'Scheduled', sending: t.admin.campaigns?.sending || 'Sending', sent: t.admin.campaigns?.sent || 'Sent', failed: t.admin.campaigns?.failed || 'Failed', paused: t.admin.campaigns?.paused || 'Paused' };
    return <span className={`badge bg-${colors[status] || 'secondary'}`}>{labels[status] || status}</span>;
  };

  // Load campaigns on mount
  useEffect(() => {
    loadCampaigns();
  }, []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h5 style={{ color: '#E8ECF0', margin: 0 }}>{t.admin.campaigns?.title || 'Email Campaigns'}</h5>
        {campaignView === 'list' && (
          <button className="btn btn-primary btn-sm" onClick={() => openCampaignEditor()}>
            + {t.admin.campaigns?.newCampaign || 'New Campaign'}
          </button>
        )}
        {campaignView !== 'list' && (
          <button className="btn btn-outline-secondary btn-sm" onClick={() => { setCampaignView('list'); setCampaignMsg({ type: '', text: '' }); }}>
            {t.admin.campaigns?.backToList || 'Back to Campaigns'}
          </button>
        )}
      </div>

      {campaignMsg.text && (
        <div className={`alert alert-${campaignMsg.type} alert-dismissible`} role="alert">
          {campaignMsg.text}
          <button type="button" className="btn-close" onClick={() => setCampaignMsg({ type: '', text: '' })}></button>
        </div>
      )}

      {recipientCount !== null && campaignView === 'list' && (
        <div className="alert alert-info py-2" style={{ fontSize: '0.85rem' }}>
          {(t.admin.campaigns?.recipientCount || 'Will be sent to {count} customers').replace('{count}', recipientCount)}
        </div>
      )}

      {quotaInfo && campaignView === 'list' && (
        <div className="mb-3 p-3" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span style={{ color: '#94A3B8', fontSize: '0.8rem' }}>
              {(t.admin.campaigns?.quotaInfo || 'Daily quota: {sent}/{limit} sent ({remaining} remaining)')
                .replace('{sent}', quotaInfo.sent).replace('{limit}', quotaInfo.limit).replace('{remaining}', quotaInfo.remaining)}
            </span>
          </div>
          <div style={{ background: 'rgba(148,163,184,0.2)', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(100, (quotaInfo.sent / quotaInfo.limit) * 100)}%`,
              background: quotaInfo.remaining <= 0 ? '#D42B2B' : '#22c55e',
              height: '100%', transition: 'width 0.3s'
            }} />
          </div>
        </div>
      )}

      {/* Campaign List */}
      {campaignView === 'list' && (
        <div className="table-responsive">
          {campaigns.length === 0 ? (
            <p style={{ color: '#94A3B8' }}>{t.admin.campaigns?.noCampaigns || 'No campaigns yet'}</p>
          ) : (
            <table className="table table-dark table-hover">
              <thead>
                <tr>
                  <th>{t.admin.campaigns?.subjectFr || 'Subject (FR)'}</th>
                  <th>{t.admin.campaigns?.status || 'Status'}</th>
                  <th>{t.admin.campaigns?.recipients || 'Recipients'}</th>
                  <th>{t.admin.campaigns?.sentCount || 'Sent'}</th>
                  <th>{t.admin.campaigns?.created || 'Created'}</th>
                  <th>{t.admin.tableHeaders?.actions || 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(c => (
                  <tr key={c.id}>
                    <td>{c.subject_fr || c.subject_en}</td>
                    <td>{campaignStatusBadge(c.status)}</td>
                    <td>{c.total_recipients}</td>
                    <td>{c.sent_count}/{c.total_recipients} {c.failed_count > 0 && <span className="text-danger">({c.failed_count} failed)</span>}</td>
                    <td>{formatDate(c.created_at)}</td>
                    <td>
                      {c.status === 'draft' && (
                        <>
                          <button className="btn btn-outline-primary btn-sm me-1" onClick={() => openCampaignEditor(c)} title={t.common?.edit || 'Edit'}>&#9998;</button>
                          <button className="btn btn-outline-danger btn-sm me-1" onClick={() => deleteCampaign(c.id)} title={t.admin.campaigns?.delete || 'Delete'}>&times;</button>
                        </>
                      )}
                      {c.status === 'paused' && (
                        <button className="btn btn-outline-warning btn-sm me-1" onClick={() => resumeCampaign(c.id)}>
                          {t.admin.campaigns?.resume || 'Resume'}
                        </button>
                      )}
                      {(c.status === 'sent' || c.status === 'sending' || c.status === 'paused') && (
                        <button className="btn btn-outline-info btn-sm" onClick={() => viewCampaignDetail(c.id)}>
                          {t.admin.campaigns?.viewDetails || 'Details'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Campaign Editor */}
      {campaignView === 'editor' && (
        <div className="card" style={{ background: '#151e2d', border: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="card-body">
            <h6 style={{ color: '#E8ECF0' }}>{editingCampaign ? (t.admin.campaigns?.editCampaign || 'Edit Campaign') : (t.admin.campaigns?.newCampaign || 'New Campaign')}</h6>

            {/* Language tabs */}
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button className={`nav-link ${campaignLangTab === 'fr' ? 'active' : ''}`} onClick={() => setCampaignLangTab('fr')}>
                  {t.admin.campaigns?.langTabFr || 'French'}
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${campaignLangTab === 'en' ? 'active' : ''}`} onClick={() => setCampaignLangTab('en')}>
                  {t.admin.campaigns?.langTabEn || 'English'}
                </button>
              </li>
              <li className="nav-item ms-auto">
                <button className="btn btn-outline-secondary btn-sm mt-1" onClick={() => campaignAutoTranslate(campaignLangTab)} disabled={campaignTranslating}>
                  {campaignTranslating ? (t.admin.campaigns?.translating || 'Translating...') : (t.admin.campaigns?.autoTranslate || 'Auto-translate')}
                </button>
              </li>
            </ul>

            {campaignLangTab === 'fr' ? (
              <>
                <div className="mb-3">
                  <label className="form-label">{t.admin.campaigns?.subjectFr || 'Subject (FR)'}</label>
                  <input type="text" className="form-control" value={campaignForm.subject_fr} onChange={e => setCampaignForm({ ...campaignForm, subject_fr: e.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="form-label">{t.admin.campaigns?.bodyFr || 'Body (FR)'}</label>
                  <textarea className="form-control" rows={8} value={campaignForm.body_fr} onChange={e => setCampaignForm({ ...campaignForm, body_fr: e.target.value })} />
                  <div className="form-text">HTML supported</div>
                </div>
              </>
            ) : (
              <>
                <div className="mb-3">
                  <label className="form-label">{t.admin.campaigns?.subjectEn || 'Subject (EN)'}</label>
                  <input type="text" className="form-control" value={campaignForm.subject_en} onChange={e => setCampaignForm({ ...campaignForm, subject_en: e.target.value })} />
                </div>
                <div className="mb-3">
                  <label className="form-label">{t.admin.campaigns?.bodyEn || 'Body (EN)'}</label>
                  <textarea className="form-control" rows={8} value={campaignForm.body_en} onChange={e => setCampaignForm({ ...campaignForm, body_en: e.target.value })} />
                  <div className="form-text">HTML supported</div>
                </div>
              </>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
              <button className="btn btn-primary btn-sm" onClick={saveCampaignDraft} disabled={campaignSaving}>
                {campaignSaving ? '...' : (t.admin.campaigns?.saveDraft || 'Save Draft')}
              </button>
              {editingCampaign && (
                <>
                  <button className="btn btn-outline-info btn-sm" onClick={() => sendCampaignTest(editingCampaign.id)}>
                    {t.admin.campaigns?.sendTest || 'Send Test'}
                  </button>
                  <button className="btn btn-success btn-sm" onClick={() => sendCampaignNow(editingCampaign.id)}>
                    {t.admin.campaigns?.sendNow || 'Send Now'}
                  </button>
                  <div className="d-flex align-items-center gap-2">
                    <input type="datetime-local" className="form-control form-control-sm" style={{ width: 'auto' }} value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} />
                    <button className="btn btn-outline-warning btn-sm" onClick={() => scheduleCampaign(editingCampaign.id)} disabled={!scheduleDate}>
                      {t.admin.campaigns?.schedule || 'Schedule'}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Preview */}
            {(campaignForm.body_fr || campaignForm.body_en) && (
              <div style={{ marginTop: '1.5rem' }}>
                <h6 style={{ color: '#94A3B8' }}>{t.admin.campaigns?.preview || 'Preview'}</h6>
                <div style={{ background: '#fff', color: '#333', padding: '1.5rem', borderRadius: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                  <h4 style={{ color: '#0C1220' }}>{campaignLangTab === 'fr' ? campaignForm.subject_fr : campaignForm.subject_en}</h4>
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(campaignLangTab === 'fr' ? campaignForm.body_fr : campaignForm.body_en) }} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Campaign Detail */}
      {campaignView === 'detail' && campaignDetail && (
        <div>
          <div className="card mb-3" style={{ background: '#151e2d', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="card-body">
              <h6 style={{ color: '#E8ECF0' }}>{campaignDetail.subject_fr || campaignDetail.subject_en}</h6>
              <div className="d-flex gap-3 mb-2">
                {campaignStatusBadge(campaignDetail.status)}
                {campaignDetail.sent_at && <small style={{ color: '#94A3B8' }}>{formatDateTime(campaignDetail.sent_at)}</small>}
              </div>

              {/* Stats */}
              <div className="row g-2 mb-3">
                <div className="col-3">
                  <div className="text-center p-2" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '1.5rem', color: '#E8ECF0', fontWeight: 600 }}>{campaignDetail.total_recipients}</div>
                    <small style={{ color: '#94A3B8' }}>{t.admin.campaigns?.recipients || 'Recipients'}</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="text-center p-2" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '1.5rem', color: '#22c55e', fontWeight: 600 }}>{campaignDetail.sent_count}</div>
                    <small style={{ color: '#94A3B8' }}>{t.admin.campaigns?.sentCount || 'Sent'}</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="text-center p-2" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '1.5rem', color: '#D42B2B', fontWeight: 600 }}>{campaignDetail.failed_count}</div>
                    <small style={{ color: '#94A3B8' }}>{t.admin.campaigns?.failedCount || 'Failed'}</small>
                  </div>
                </div>
                <div className="col-3">
                  <div className="text-center p-2" style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '1.5rem', color: '#3b82f6', fontWeight: 600 }}>{(campaignDetail.recipients || []).filter(r => r.opened_at).length}</div>
                    <small style={{ color: '#94A3B8' }}>{t.admin.campaigns?.openedCount || 'Opened'}</small>
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              {campaignDetail.status === 'sending' && campaignDetail.total_recipients > 0 && (
                <div className="progress mb-3" style={{ height: '6px' }}>
                  <div className="progress-bar bg-success" style={{ width: `${(campaignDetail.sent_count / campaignDetail.total_recipients * 100).toFixed(0)}%` }}></div>
                </div>
              )}

              {/* Recipients table */}
              {campaignDetail.recipients && campaignDetail.recipients.length > 0 && (
                <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  <table className="table table-dark table-sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>{t.admin.campaigns?.status || 'Status'}</th>
                        <th>{t.admin.campaigns?.openedCount || 'Opened'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaignDetail.recipients.map(r => (
                        <tr key={r.id}>
                          <td>{r.first_name} {r.last_name}</td>
                          <td>{r.email}</td>
                          <td>
                            <span className={`badge bg-${r.status === 'sent' ? 'success' : r.status === 'failed' ? 'danger' : 'secondary'}`}>
                              {r.status}
                            </span>
                            {r.error_text && <small className="text-danger d-block">{r.error_text}</small>}
                          </td>
                          <td>{r.opened_at ? formatDateTime(r.opened_at) : '\u2014'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
